// import helpers
import { useAuthStore } from '@bv/stores/auth';
import { useUserStore } from '@bv/stores/user';
import { io, Socket } from 'socket.io-client';

// import stores
import { getItem } from '@/helpers/cookies/cookies.helper';
import { useConversationsStore } from '@/stores/conversations/conversations.store';

// import callbacks methods
import aiStatusUpdate from './events/ai-status-update';
import assignTag from './events/assign-tag';
import assignedTeam from './events/assigned-team';
import assignedUser from './events/assigned-user';
import closeSession from './events/close-session';
import contactDelete from './events/contact-delete';
import contactUpdate from './events/contact-update';
import messageUpdate from './events/message-update';
import messagesStatusUpdate from './events/messages-status-update';
import newConversation from './events/new-conversation';
import newMessage from './events/new-message';
import openConversation from './events/open-conversation';
import participantsCreate from './events/participants-create';
import pendedConversation from './events/pended-conversation';
import quotaAlert from './events/quota-alert';
import resolveConversation from './events/resolve-conversation';
import retryMessageMedia from './events/retry-message-media';
import setPriority from './events/set-priority';
import startTyping from './events/start-typing';
import stopTyping from './events/stop-typing';
import unassignTag from './events/unassign-tag';
import unreadMessagesUpdate from './events/unread-messages-update';
import updateConversationsCount from './events/update-conversations-count';
import workflowStatusUpdate from './events/workflow-status-update';

interface InitOptions {
  options?: Partial<SocketOptions>;
}

interface SocketOptions {
  autoConnect: boolean;
  reconnection: boolean;
  reconnectionAttempts: number;
  [key: string]: any;
}

type Callback = (data: any) => void;

export class ConversationsSocketHandler {
  private socket: Socket | null = null;
  readonly socketUrl: string = import.meta.env.VITE_SOCKET_URL;
  // list of all events name
  private events: string[] = [
    'CONVERSATIONS.ASSIGNED.TEAM',
    'CONVERSATIONS.ASSIGNED.USER',
    'CONVERSATIONS.PARTICIPANTS.CREATE',
    'CONVERSATIONS.UNREAD_MESSAGES.INCREMENT',
    'CONVERSATIONS.STATUS.OPENED',
    'CONVERSATIONS.STATUS.RESOLVED',
    'CONVERSATIONS.STATUS.PENDED',
    'CONVERSATIONS.SET_PRIORITY',
    'CONVERSATIONS.CONTACT.ASSIGN_TAG',
    'CONVERSATIONS.CONTACT.DELETE',
    'CONVERSATIONS.NEW',
    'CONVERSATIONS.NEW_MESSAGE',
    'CONVERSATIONS.CONTACT.UNASSIGN_TAG',
    'CONVERSATIONS.CONTACT.UPDATE',
    'SOCKET.MESSAGE.STATUS.UPDATE',
    'CONVERSATIONS.RETRY_MEDIA',
    'WORKFLOWS.STATUS.UPDATED',
    'CONVERSATIONS.MESSAGE.UPDATE',
    'CONVERSATIONS.START_TYPING',
    'CONVERSATIONS.STOP_TYPING',
    'CONVERSATION.SESSION_END',
    'CONVERSATION_COUNT_ALL_UNASSIGNED',
    'CONVERSATION_COUNT_ME',
    'CONVERSATION.AI_CONVERSATION',
    'CONVERSATION.QUOTA_THRESHOLD_ALERT',
  ];
  // list of all events callback methods
  private callbacks: Record<string, Callback> = {
    'CONVERSATIONS.NEW_MESSAGE': newMessage,
    'CONVERSATIONS.NEW': newConversation,
    'CONVERSATIONS.SET_PRIORITY': setPriority,
    'CONVERSATIONS.UNREAD_MESSAGES.INCREMENT': unreadMessagesUpdate,
    'CONVERSATIONS.STATUS.RESOLVED': resolveConversation,
    'CONVERSATIONS.STATUS.PENDED': pendedConversation,
    'CONVERSATIONS.STATUS.OPENED': openConversation,
    'CONVERSATIONS.ASSIGNED.TEAM': assignedTeam,
    'CONVERSATIONS.ASSIGNED.USER': assignedUser,
    'CONVERSATIONS.PARTICIPANTS.CREATE': participantsCreate,
    'CONVERSATIONS.CONTACT.ASSIGN_TAG': assignTag,
    'CONVERSATIONS.CONTACT.UNASSIGN_TAG': unassignTag,
    'CONVERSATIONS.CONTACT.DELETE': contactDelete,
    'CONVERSATIONS.CONTACT.UPDATE': contactUpdate,
    'SOCKET.MESSAGE.STATUS.UPDATE': messagesStatusUpdate,
    'CONVERSATIONS.RETRY_MEDIA': retryMessageMedia,
    'WORKFLOWS.STATUS.UPDATED': workflowStatusUpdate,
    'CONVERSATIONS.MESSAGE.UPDATE': messageUpdate,
    'CONVERSATIONS.START_TYPING': startTyping,
    'CONVERSATIONS.STOP_TYPING': stopTyping,
    'CONVERSATION.SESSION_END': closeSession,
    CONVERSATION_COUNT_ALL_UNASSIGNED: updateConversationsCount,
    CONVERSATION_COUNT_ME: updateConversationsCount,
    'CONVERSATION.AI_CONVERSATION': aiStatusUpdate,
    'CONVERSATION.QUOTA_THRESHOLD_ALERT': quotaAlert,
  };
  private conversationsStore: ReturnType<typeof useConversationsStore> | null = null;
  private useUserStore: ReturnType<typeof useUserStore> | null = null;

  // reconnection retry
  private connectionRetryCount: number = 0;
  private readonly maxRetries: number = 5;

  init({ options = {} }: InitOptions = {}): void {
    if (this.socket) {
      return;
    } // Prevent re-init

    this.conversationsStore = useConversationsStore();
    this.useUserStore = useUserStore();

    this.socket = io(`${this.socketUrl}`, {
      transports: ['websocket'], // <--- Force WebSocket only
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxRetries,
      reconnectionDelay: 1000, // ms between tries
      timeout: 20000, // connection timeout
      auth: {
        token:
          (import.meta.env.VITE_IMPERSONATE === 'true' &&
            (useAuthStore().impersonateToken || getItem('impersonate_token'))) ||
          useAuthStore().token ||
          getItem('token'),
        workspaceId: this.useUserStore?.currentWorkspace?.id,
      },
      ...options,
    });

    this.setupConnectionListeners();
    this.registerEventListeners();
  }

  private setupConnectionListeners(): void {
    (this.socket as Socket).on('connect', this.onOpen.bind(this));
    (this.socket as Socket).on('disconnect', this.onClose.bind(this));
    (this.socket as Socket).on('connect_error', this.onError.bind(this));
  }

  private registerEventListeners(): void {
    this.events.forEach((eventName: string) => {
      (this.socket as Socket).on(eventName, (data: any) => this.onEvent(eventName, data));
    });
  }

  private onEvent(eventName: string, data: any): void {
    if (this.callbacks[eventName]) {
      this.callbacks[eventName]({ ...data.body, timestamp: data.timestamp });
    } else {
      // logic here
    }
  }

  private onOpen(): void {
    this.conversationsStore?.toggleSocketConnection();
    this.connectionRetryCount = 0;
    this.sendAction('CONVERSATION_CURRENT_FILTER', this.conversationsStore?.filtrationParams);

    const activeConversation = this.conversationsStore?.activeConversation;
    if (activeConversation) {
      this.sendAction('CONVERSATIONS.JOIN_CONVERSATION', {
        conversationId: activeConversation.conversationId,
      });
    }
  }

  private onClose(): void {
    this.conversationsStore?.toggleSocketConnection();
  }

  reconnectionAttempt(): void {
    if (this.socket) {
      if (this.socket.connected) {
        return;
      }

      this.connectionRetryCount = 0;
      this.conversationsStore?.toggleSocketReconnectionPopup();
      this.socket.disconnect(); // force reset internal state
      this.socket.connect(); // now reconnect from scratch
    } else {
      this.init(); // recreate from scratch if no socket
    }
  }

  private onError(error: any): void {
    if (++this.connectionRetryCount === this.maxRetries) {
      this.conversationsStore?.saveSocketError(error);
      this.conversationsStore?.toggleSocketReconnectionPopup();
    }
  }

  on(eventName: string, callback: Callback): void {
    this.callbacks[eventName] = callback;
  }

  off(eventName: string): void {
    delete this.callbacks[eventName];
    (this.socket as Socket).off(eventName);
  }

  sendAction(name: string, data: any): void {
    if (!this.socket) {
      return;
    }
    this.socket.emit(name, data);
  }

  destroy(resetStore?: boolean): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    if (resetStore) {
      this.conversationsStore?.resetStore();
    }
  }
}

const conversationsSocket = new ConversationsSocketHandler();

export default conversationsSocket;
